import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Brain, Plus, X, AlertCircle, Clock, CheckCircle, ChevronDown, ChevronUp, User,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  getClients, createClient, getSessions, createSession,
  type CoachingClient, type CoachingSession,
} from '../../lib/coaching-sessions';
import { SessionIntelligencePanel } from '../../components/coach/SessionIntelligencePanel';

export function SessionIntelligence() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [clients, setClients] = useState<CoachingClient[]>([]);
  const [sessions, setSessions] = useState<CoachingSession[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Add client modal
  const [showAddClient, setShowAddClient] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [clientForm, setClientForm] = useState({ full_name: '', email: '' });
  const [formError, setFormError] = useState<string | null>(null);

  // Panel state
  const [intelligenceSession, setIntelligenceSession] = useState<CoachingSession | null>(null);
  const [panelClientName, setPanelClientName] = useState('');
  const [panelClientGoals, setPanelClientGoals] = useState<string | undefined>();

  // Per-client history expand state
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  // Creating session in background
  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/si/login'); return; }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) { navigate('/si/login'); return; }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) { navigate('/si/login'); return; }
        setCoachId(coach.id);

        const [c, s] = await Promise.all([getClients(coach.id), getSessions(coach.id)]);
        setClients(c);
        setSessions(s);
      } catch {
        setError('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const sessionsByClient = sessions.reduce<Record<string, CoachingSession[]>>((acc, s) => {
    if (!acc[s.client_id]) acc[s.client_id] = [];
    acc[s.client_id].push(s);
    return acc;
  }, {});

  const handleAddClient = async () => {
    if (!coachId || !clientForm.full_name.trim()) {
      setFormError('Name is required');
      return;
    }
    setFormError(null);
    setAddingClient(true);
    try {
      const { data, error: err } = await createClient({
        coach_id: coachId,
        full_name: clientForm.full_name.trim(),
        email: clientForm.email.trim(),
      });
      if (err) throw err;
      if (data) {
        setClients(prev => [data as CoachingClient, ...prev]);
        setClientForm({ full_name: '', email: '' });
        setShowAddClient(false);
      }
    } catch {
      setFormError('Failed to add client. Please try again.');
    } finally {
      setAddingClient(false);
    }
  };

  const handleNewAnalysis = async (client: CoachingClient) => {
    if (!coachId) return;
    setCreatingFor(client.id);
    try {
      const { data, error: err } = await createSession({
        client_id: client.id,
        coach_id: coachId,
        session_date: new Date().toISOString().slice(0, 10),
        duration_minutes: 60,
        session_type: 'video',
      });
      if (err) throw err;
      if (data) {
        const newSession = data as unknown as CoachingSession;
        setSessions(prev => [newSession, ...prev]);
        setPanelClientName(client.full_name);
        setPanelClientGoals(client.goals ?? undefined);
        setIntelligenceSession(newSession);
      }
    } catch {
      setError('Failed to start analysis. Please try again.');
    } finally {
      setCreatingFor(null);
    }
  };

  const handleOpenPastSession = (session: CoachingSession, client: CoachingClient) => {
    setPanelClientName(client.full_name);
    setPanelClientGoals(client.goals ?? undefined);
    setIntelligenceSession(session);
  };

  const toggleClientHistory = (clientId: string) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const totalAnalysed = sessions.filter(s => s.analysis_status === 'complete').length;

  if (authLoading || isLoading) return <LoadingSpinner message="Loading Session Intelligence..." />;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-teal-500 rounded-xl shadow-soft">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">Session Intelligence™</h1>
                <p className="freud-text-sm text-chocolate-500">
                  Select a client to analyse their session — patterns, insights, next-session prep
                </p>
              </div>
            </div>
            <button
              onClick={() => { setShowAddClient(true); setFormError(null); }}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 transition-colors shadow-soft flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              Add Client
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {error && (
          <div className="bg-coral-50 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Stats bar */}
        {clients.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            <div className="freud-card-elevated p-4 text-center">
              <p className="text-2xl font-bold text-chocolate-900">{clients.length}</p>
              <p className="text-xs text-chocolate-500 mt-0.5">Clients</p>
            </div>
            <div className="freud-card-elevated p-4 text-center">
              <p className="text-2xl font-bold text-chocolate-900">{sessions.length}</p>
              <p className="text-xs text-chocolate-500 mt-0.5">Sessions</p>
            </div>
            <div className="freud-card-elevated p-4 text-center">
              <p className="text-2xl font-bold text-teal-600">{totalAnalysed}</p>
              <p className="text-xs text-chocolate-500 mt-0.5">Analysed</p>
            </div>
          </div>
        )}

        {/* Client cards */}
        {clients.length === 0 ? (
          <div className="freud-card-elevated p-12 text-center">
            <div className="p-4 bg-teal-50 rounded-full w-fit mx-auto mb-4">
              <Brain className="w-10 h-10 text-teal-400" />
            </div>
            <p className="text-base font-semibold text-chocolate-800 mb-1">No clients yet</p>
            <p className="text-sm text-chocolate-500 mb-5 max-w-sm mx-auto">
              Add your first client to start analysing sessions with AI.
            </p>
            <button
              onClick={() => { setShowAddClient(true); setFormError(null); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add First Client
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map(client => {
              const clientSessions = sessionsByClient[client.id] || [];
              const completedSessions = clientSessions.filter(s => s.analysis_status === 'complete');
              const isExpanded = expandedClients.has(client.id);
              const isCreating = creatingFor === client.id;

              return (
                <div key={client.id} className="freud-card-elevated overflow-hidden">
                  {/* Client row */}
                  <div className="p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-cream-100 rounded-lg flex-shrink-0">
                        <User className="w-4 h-4 text-chocolate-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-chocolate-900 truncate">{client.full_name}</p>
                        {client.email && (
                          <p className="text-xs text-chocolate-400 truncate">{client.email}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {completedSessions.length > 0 && (
                        <button
                          onClick={() => toggleClientHistory(client.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-chocolate-600 bg-cream-100 hover:bg-cream-200 rounded-lg transition-colors border border-cream-300"
                        >
                          <Clock className="w-3.5 h-3.5" />
                          {completedSessions.length} past
                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      <button
                        onClick={() => handleNewAnalysis(client)}
                        disabled={isCreating}
                        className="flex items-center gap-2 px-4 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isCreating ? (
                          <>
                            <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            Starting...
                          </>
                        ) : (
                          <>
                            <Brain className="w-3.5 h-3.5" />
                            New Analysis
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Past sessions for this client */}
                  <AnimatePresence>
                    {isExpanded && completedSessions.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-cream-200 divide-y divide-cream-100">
                          {completedSessions.map(session => (
                            <button
                              key={session.id}
                              onClick={() => handleOpenPastSession(session, client)}
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-cream-50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-3">
                                <div className="p-1.5 bg-teal-50 rounded-lg">
                                  <CheckCircle className="w-3.5 h-3.5 text-teal-600" />
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-chocolate-800">
                                    {new Date(session.session_date).toLocaleDateString('en-US', {
                                      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                                    })}
                                  </p>
                                  {session.session_analysis?.session_summary?.main_topic && (
                                    <p className="text-xs text-chocolate-400 italic mt-0.5 truncate max-w-xs">
                                      "{session.session_analysis.session_summary.main_topic}"
                                    </p>
                                  )}
                                </div>
                              </div>
                              <span className="text-xs text-teal-600 font-medium">View Analysis</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Add Client Modal */}
      <AnimatePresence>
        {showAddClient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={e => { if (e.target === e.currentTarget) setShowAddClient(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-semibold text-chocolate-900">Add Client</h2>
                <button onClick={() => setShowAddClient(false)} className="p-1.5 hover:bg-cream-100 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-chocolate-500" />
                </button>
              </div>

              {formError && (
                <div className="mb-4 bg-coral-50 border border-coral-200 text-coral-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {formError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-chocolate-700 mb-1.5">
                    Full Name <span className="text-coral-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={clientForm.full_name}
                    onChange={e => setClientForm(p => ({ ...p, full_name: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddClient(); }}
                    placeholder="e.g. Jane Smith"
                    className="freud-input w-full text-sm"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-chocolate-700 mb-1.5">
                    Email <span className="text-chocolate-400">(optional)</span>
                  </label>
                  <input
                    type="email"
                    value={clientForm.email}
                    onChange={e => setClientForm(p => ({ ...p, email: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddClient(); }}
                    placeholder="e.g. jane@example.com"
                    className="freud-input w-full text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddClient(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-chocolate-700 bg-cream-100 hover:bg-cream-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddClient}
                  disabled={addingClient || !clientForm.full_name.trim()}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {addingClient ? 'Adding...' : 'Add Client'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Session Intelligence Panel */}
      <AnimatePresence>
        {intelligenceSession && coachId && (
          <SessionIntelligencePanel
            session={sessions.find(s => s.id === intelligenceSession.id) || intelligenceSession}
            coachId={coachId}
            clientName={panelClientName}
            clientGoals={panelClientGoals}
            onClose={() => setIntelligenceSession(null)}
            onUpdate={(sessionId, updates) => {
              setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, ...updates } : s));
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
